import type { AnalysisSearch } from '@/routes/agent_spa/agent/analysis';
import useFetchAnalysisFilterOptions from '../api/useFetchAnalysisFilterOptions';
import useFetchAnalysisInsight from '../api/useFetchAnalysisInsight';
import useFetchAnalysisSales from '../api/useFetchAnalysisSales';
import useFetchAnalysisSalesDetails from '../api/useFetchAnalysisSalesDetails';
import AnalysisFilterBar from './AnalysisFilterBar';
import InsightLeadTimeView from './insight/InsightLeadTimeView';
import SalesView from './insight/sales/SalesView';
import { INSIGHT_FILTER_PANEL_DEFINITIONS } from './search/filterPanelDefinitions';

// インサイトタブでは求職者担当者グループのみサポートする
const INSIGHT_GROUP_BY_OPTIONS = [{ value: 'candidate_user', label: '求職者担当者' }] as const;

type Props = {
  search: AnalysisSearch;
  onUpdateSearch: (updates: Partial<AnalysisSearch>) => void;
};

/**
 * インサイトタブ。
 * 上部カードのドロップダウンで「リードタイム」と「売上」のビューを切り替える。
 */
export default function AnalysisInsightTab({ search, onUpdateSearch }: Props) {
  const {
    from,
    to,
    granularity,
    metric,
    count_mode: countMode,
    group_by: groupBy,
    insight_metric: insightMetric,
    sales_trend_mode: salesTrendMode,
    page,
    filter_organization_user_ids: filterOrgUserIds,
    filter_target: filterTarget,
    filter_company_tag_ids: filterCompanyTagIds,
    filter_job_tag_ids: filterJobTagIds,
    filter_candidate_tag_ids: filterCandidateTagIds,
  } = search;

  const isSales = insightMetric === 'sales';

  const { data: filterOptions } = useFetchAnalysisFilterOptions();

  // インサイトでは group_by に candidate_user のみ受け付ける（API 側の仕様）
  const insightGroupBy = groupBy === 'candidate_user' ? 'candidate_user' : undefined;
  const isGrouped = insightGroupBy !== undefined;

  const insightQuery = useFetchAnalysisInsight(
    {
      from,
      to,
      granularity,
      metric,
      count_mode: countMode,
      group_by: insightGroupBy,
      filter_target: filterTarget,
      filter_organization_user_ids: filterOrgUserIds,
      filter_company_tag_ids: filterCompanyTagIds,
      filter_job_tag_ids: filterJobTagIds,
      filter_candidate_tag_ids: filterCandidateTagIds,
    },
    { enabled: !isSales }
  );

  const salesParams = {
    from,
    to,
    granularity,
    filter_target: filterTarget,
    filter_organization_user_ids: filterOrgUserIds,
    filter_company_tag_ids: filterCompanyTagIds,
    filter_job_tag_ids: filterJobTagIds,
    filter_candidate_tag_ids: filterCandidateTagIds,
  };
  const salesQuery = useFetchAnalysisSales(salesParams, { enabled: isSales });
  const salesDetailsQuery = useFetchAnalysisSalesDetails(
    { ...salesParams, page },
    { enabled: isSales }
  );

  const onMetricChange = (v: 'lead_time' | 'sales') =>
    onUpdateSearch({ insight_metric: v, page: 1 });

  const filterBar = (
    <div className="sticky top-[60px] z-20 -mx-lg bg-[#f6f6f6] px-lg py-3 shadow-[0_2px_4px_rgba(0,0,0,0.06)]">
      <AnalysisFilterBar
        filterOrganizationUserIds={filterOrgUserIds ?? []}
        organizationUsers={filterOptions?.organization_users ?? []}
        filterCompanyTagIds={filterCompanyTagIds ?? []}
        companyTags={filterOptions?.company_tags ?? []}
        filterJobTagIds={filterJobTagIds ?? []}
        jobTags={filterOptions?.job_tags ?? []}
        filterCandidateTagIds={filterCandidateTagIds ?? []}
        candidateTags={filterOptions?.candidate_tags ?? []}
        groupBy={groupBy}
        metric={metric}
        from={from}
        to={to}
        granularity={granularity}
        countMode={countMode}
        filterTarget={filterTarget}
        onFilterChange={({
          filterTarget: nextFilterTarget,
          organizationUserIds,
          companyTagIds,
          jobTagIds,
          candidateTagIds,
        }) =>
          onUpdateSearch({
            filter_target: nextFilterTarget,
            filter_organization_user_ids: organizationUserIds,
            filter_company_tag_ids: companyTagIds,
            filter_job_tag_ids: jobTagIds,
            filter_candidate_tag_ids: candidateTagIds,
          })
        }
        onGroupByChange={(v) => onUpdateSearch({ group_by: v })}
        onMetricChange={(v) => onUpdateSearch({ metric: v })}
        onDateRangeChange={(nextFrom, nextTo) => onUpdateSearch({ from: nextFrom, to: nextTo })}
        onGranularityChange={(v) => onUpdateSearch({ granularity: v })}
        onCountModeChange={(v) => onUpdateSearch({ count_mode: v })}
        showMetricToggle={false}
        showCountModeToggle={false}
        availableGroupByOptions={INSIGHT_GROUP_BY_OPTIONS}
        availableFilterPanels={INSIGHT_FILTER_PANEL_DEFINITIONS}
      />
    </div>
  );

  const activeQuery = isSales ? salesQuery : insightQuery;

  if (activeQuery.isPending) {
    return (
      <>
        {filterBar}
        <div className="mt-md rounded-md border border-border bg-white p-12 text-center text-sm text-sub">
          読み込み中...
        </div>
      </>
    );
  }

  if (activeQuery.isError || !activeQuery.data) {
    return (
      <>
        {filterBar}
        <div className="mt-md rounded-md border border-border bg-white p-12 text-center text-sm text-accent1">
          {isSales ? '売上データの取得に失敗しました' : 'インサイトデータの取得に失敗しました'}
        </div>
      </>
    );
  }

  return (
    <>
      {filterBar}

      {isSales ? (
        <SalesView
          sales={salesQuery.data!}
          details={salesDetailsQuery.data}
          trendMode={salesTrendMode}
          onTrendModeChange={(v) => onUpdateSearch({ sales_trend_mode: v })}
          page={page}
          onPageChange={(p) => onUpdateSearch({ page: p })}
          insightMetric={insightMetric}
          onMetricChange={onMetricChange}
        />
      ) : (
        <InsightLeadTimeView
          insight={insightQuery.data!}
          isGrouped={isGrouped}
          insightMetric={insightMetric}
          onMetricChange={onMetricChange}
        />
      )}
    </>
  );
}
